import { useEffect, useRef, useState } from 'react'

// Keep DOM slots fixed during the gesture. Only transforms move, and the owner
// commits one permutation on release (never a persistence write per pointer move).
export function useSmoothReorder(ids, onReorder, { disabled = false, scope = '', groupForId = () => '', settleMs = 0 } = {}) {
  const nodes = useRef(new Map())
  const drag = useRef(null)
  const suppressClick = useRef(false)
  const settleTimer = useRef(null)
  const [preview, setPreview] = useState(null)
  const identity = JSON.stringify(ids.map(id => [id, groupForId(id)]))
  const cancel = () => { clearTimeout(settleTimer.current); drag.current = null; setPreview(null) }
  useEffect(() => {
    cancel()
  }, [identity, disabled, scope])
  useEffect(() => {
    const escape = event => {
      if (event.key === 'Escape' && drag.current) {
        event.preventDefault(); event.stopPropagation(); cancel()
      }
    }
    window.addEventListener('keydown', escape, true)
    window.addEventListener('blur', cancel)
    return () => {
      window.removeEventListener('keydown', escape, true)
      window.removeEventListener('blur', cancel)
      clearTimeout(settleTimer.current)
      drag.current = null
    }
  }, [])

  function update(clientY) {
    const current = drag.current
    if (!current || current.settling) return
    current.clientY = clientY
    const scroll = current.scroller?.scrollTop || 0
    const delta = clientY - current.startY + scroll - current.startScroll
    if (Math.abs(delta) < 4 && !current.moved) return
    current.moved = true
    const { order, boxes, id } = current
    const first = boxes.get(order[0]), last = boxes.get(order.at(-1)), box = boxes.get(id)
    const y = Math.max(first.top, Math.min(last.bottom - box.height, box.top + delta))
    const others = order.filter(key => key !== id)
    let index = 0, slotTop = first.top, distance = Infinity
    for (let candidate = 0; candidate <= others.length; candidate += 1) {
      const nextDistance = Math.abs(y - slotTop)
      if (nextDistance < distance) { index = candidate; distance = nextDistance }
      if (candidate < others.length) slotTop += boxes.get(others[candidate]).height
    }
    const next = [...others]; next.splice(index, 0, id)
    let top = first.top
    const offsets = {}
    for (const key of next) {
      offsets[key] = key === id ? y - box.top : top - boxes.get(key).top
      top += boxes.get(key).height
    }
    current.next = next
    setPreview({ id, offsets })
  }

  useEffect(() => {
    if (!preview) return
    let frame, lastTime
    const tick = time => {
      const current = drag.current
      if (!current || current.settling) return
      const scroller = current.scroller
      if (scroller) {
        const rect = scroller.getBoundingClientRect()
        const direction = current.clientY < rect.top + 28 ? -1 : current.clientY > rect.bottom - 28 ? 1 : 0
        const elapsed = lastTime == null ? 0 : Math.min(32, time - lastTime)
        const previous = scroller.scrollTop
        scroller.scrollTop += direction * elapsed * 0.4
        if (previous !== scroller.scrollTop) update(current.clientY)
      }
      lastTime = time
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [Boolean(preview)])

  function start(event, id) {
    suppressClick.current = false
    if (disabled || drag.current?.settling || event.button !== 0) return
    const order = ids.filter(key => groupForId(key) === groupForId(id))
    if (order.length < 2) return
    const boxes = new Map()
    for (const key of order) {
      const elements = [...(nodes.current.get(key)?.values() || [])].filter(Boolean)
      const rects = elements.map(node => node.getBoundingClientRect())
      if (!rects.length) return
      const top = Math.min(...rects.map(rect => rect.top)), bottom = Math.max(...rects.map(rect => rect.bottom))
      boxes.set(key, { top, bottom, height: bottom - top })
    }
    event.preventDefault(); event.stopPropagation()
    suppressClick.current = true
    let scroller = event.currentTarget.parentElement
    while (scroller && !(scroller.scrollHeight > scroller.clientHeight && /(auto|scroll)/.test(getComputedStyle(scroller).overflowY))) scroller = scroller.parentElement
    drag.current = { id, pointerId: event.pointerId, startY: event.clientY, clientY: event.clientY, order, allIds: ids, next: order, boxes, scroller, startScroll: scroller?.scrollTop || 0, moved: false }
    event.currentTarget.setPointerCapture?.(event.pointerId)
  }

  function finish(event, cancelled = false) {
    const current = drag.current
    if (!current || current.pointerId !== event.pointerId || current.settling) return
    if (!cancelled) update(event.clientY)
    const changed = !cancelled && current.moved && current.next.some((id, index) => id !== current.order[index])
    const commit = () => {
      cancel()
      if (!changed) return
      let index = 0
      const group = new Set(current.order)
      onReorder?.(current.allIds.map(id => group.has(id) ? current.next[index++] : id), current.id)
    }
    const delay = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 0 : settleMs
    if (changed && delay > 0) {
      current.settling = true
      let top = current.boxes.get(current.order[0]).top
      const offsets = {}
      for (const id of current.next) {
        offsets[id] = top - current.boxes.get(id).top
        top += current.boxes.get(id).height
      }
      setPreview({ id: current.id, offsets, settling: true })
      settleTimer.current = setTimeout(commit, delay)
    } else commit()
    event.currentTarget.releasePointerCapture?.(event.pointerId)
  }

  return {
    start,
    cancel,
    isDragging: Boolean(preview),
    consumeClick(event) {
      if (!suppressClick.current || event.detail === 0) return false
      suppressClick.current = false
      event.preventDefault(); event.stopPropagation()
      return true
    },
    bind(id, nodeId = id) {
      return {
        ref: node => {
          if (!nodes.current.has(id)) nodes.current.set(id, new Map())
          if (node) nodes.current.get(id).set(nodeId, node)
          else { nodes.current.get(id)?.delete(nodeId); if (!nodes.current.get(id)?.size) nodes.current.delete(id) }
        },
        style: {
          transform: `translate3d(0, ${preview?.offsets[id] || 0}px, 0)`,
          position: 'relative',
          zIndex: preview?.id === id ? 10 : undefined,
          // Committing the DOM order already moves every row into its final slot.
          // Clearing the preview transform must be atomic, not a second animation.
          transition: !preview ? 'none' : preview.id === id ? (preview.settling ? `transform ${settleMs}ms cubic-bezier(0.2,0.8,0.2,1)` : 'none') : undefined,
        },
        'data-reorder-dragging': preview?.id === id || undefined,
        onPointerDownCapture: () => { suppressClick.current = false },
        onPointerMove: event => { if (drag.current?.pointerId === event.pointerId) { event.preventDefault(); update(event.clientY) } },
        onPointerUp: event => finish(event),
        onPointerCancel: event => finish(event, true),
        onLostPointerCapture: event => finish(event, true),
      }
    },
  }
}
